import React, { useMemo, useEffect, useRef, useState, useCallback } from 'react';
import { Users, ArrowLeftRight, Check } from 'lucide-react';

/**
 * Karaoke view — two render modes that share the same scroll/auto-follow shell.
 *
 * mode='words' (DEFAULT, original behavior — Russian sessions, no change):
 *   Renders `words[]` grouped into speaker-blocks, highlights one word per
 *   tick by binary-searching `currentTime` against word boundaries.
 *
 * mode='sentences' (NEW, used when viewing the Russian translation of a
 * foreign-language session — see `transcript_paragraphs_ru`):
 *   Renders `sentences[]` grouped into speaker-blocks, highlights the WHOLE
 *   sentence whose [start, end] contains currentTime. Word-level alignment
 *   between source language and Russian is not solvable in the general
 *   case, so this mode trades word-precision for reliable sentence-level
 *   "read along" sync.
 *
 * Props:
 *   mode: 'words' | 'sentences'  (default: 'words')
 *   words:     [{word, start, end, speaker?}]            (mode='words')
 *   sentences: [{text, start, end, speaker?}]            (mode='sentences')
 *   paragraphs: [{text, start, end, speaker?}]           (optional, currently unused)
 *   currentTime: number (audio current playback time in seconds)
 *   onSeek: (time: number) => void  (called when user clicks on a word/sentence)
 *   speakerLabels: {[speakerId: string]: string}
 *   onSpeakerLabelsChange: (labels) => Promise<void>
 */
const KaraokeView = ({
  mode = 'words',
  words,
  sentences,
  paragraphs,
  currentTime,
  onSeek,
  speakerLabels,
  onSpeakerLabelsChange,
}) => {
  const scrollRef = useRef(null);
  const activeWordRef = useRef(null);
  const lastScrollTimeRef = useRef(0);
  const [editingLabels, setEditingLabels] = useState(null); // null | {[id]: string}

  // Find active word index via binary search for large word lists
  const activeIndex = useMemo(() => {
    if (!words || words.length === 0) return -1;
    // Linear scan is fine for <5000 words; binary search for larger
    if (words.length < 5000) {
      for (let i = 0; i < words.length; i++) {
        if (currentTime >= words[i].start && currentTime <= words[i].end) return i;
        if (words[i].start > currentTime) return Math.max(0, i - 1);
      }
      return words.length - 1;
    }
    let lo = 0, hi = words.length - 1;
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      if (currentTime < words[mid].start) hi = mid - 1;
      else if (currentTime > words[mid].end) lo = mid + 1;
      else return mid;
    }
    return Math.max(0, hi);
  }, [words, currentTime]);

  // Auto-scroll to keep active word visible (throttled to avoid jitter)
  useEffect(() => {
    if (activeIndex < 0) return;
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 200) return; // throttle 200ms
    lastScrollTimeRef.current = now;
    const el = activeWordRef.current;
    if (!el || !scrollRef.current) return;
    const container = scrollRef.current;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    // Only act when the active word leaves the comfortable middle band
    // (20–80% of the inner container height) — avoids jitter on every word.
    const relativeTop = elRect.top - containerRect.top;
    const containerHeight = containerRect.height;
    if (relativeTop < containerHeight * 0.2 || relativeTop > containerHeight * 0.8) {
      // Scroll ONLY the inner transcript container — never the page/window.
      // Previously used el.scrollIntoView({ block: 'center' }), which
      // cascades to ALL ancestor scroll containers and was pushing the
      // waveform off the top of the screen on long sessions. Compute the
      // target scrollTop manually so the element ends up vertically centered
      // inside `container` while the document scroll position is untouched.
      const elCenterInContainer = (elRect.top - containerRect.top) + elRect.height / 2;
      const containerCenter = containerHeight / 2;
      const targetScrollTop = container.scrollTop + (elCenterInContainer - containerCenter);
      container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    }
  }, [activeIndex]);

  // Group words into paragraph-like blocks for speaker labels
  const blocks = useMemo(() => {
    if (!words || words.length === 0) return [];
    const result = [];
    let currentBlock = null;
    for (let i = 0; i < words.length; i++) {
      const w = words[i];
      const speakerKey = w.speaker != null ? String(w.speaker) : '';
      // New block on speaker change OR long pause (>1.5s between words)
      const prevEnd = currentBlock && currentBlock.words.length > 0
        ? currentBlock.words[currentBlock.words.length - 1].end
        : -Infinity;
      const longPause = (w.start - prevEnd) > 1.5;
      if (!currentBlock || currentBlock.speaker !== speakerKey || longPause) {
        if (currentBlock) result.push(currentBlock);
        currentBlock = { speaker: speakerKey, start: w.start, words: [], indexStart: i };
      }
      currentBlock.words.push(w);
    }
    if (currentBlock) result.push(currentBlock);
    return result;
  }, [words]);

  // ===== Sentence-mode plumbing (used only when mode='sentences') =====
  // Active sentence: the one whose [start, end] window contains currentTime.
  // Falls back to the latest sentence whose start <= currentTime when we're
  // in a gap between sentences (e.g. during a pause).
  const activeSentenceIndex = useMemo(() => {
    if (mode !== 'sentences' || !sentences || sentences.length === 0) return -1;
    let lastBefore = -1;
    for (let i = 0; i < sentences.length; i++) {
      const s = sentences[i];
      if (currentTime >= s.start && currentTime <= s.end) return i;
      if (s.start <= currentTime) lastBefore = i;
      else break;
    }
    return lastBefore;
  }, [mode, sentences, currentTime]);

  // Group sentences into speaker-blocks (same long-pause heuristic as words).
  const sentenceBlocks = useMemo(() => {
    if (mode !== 'sentences' || !sentences || sentences.length === 0) return [];
    const result = [];
    let currentBlock = null;
    for (let i = 0; i < sentences.length; i++) {
      const s = sentences[i];
      const speakerKey = s.speaker != null ? String(s.speaker) : '';
      const prevEnd = currentBlock && currentBlock.sentences.length > 0
        ? currentBlock.sentences[currentBlock.sentences.length - 1].end
        : -Infinity;
      const longPause = (s.start - prevEnd) > 1.5;
      if (!currentBlock || currentBlock.speaker !== speakerKey || longPause) {
        if (currentBlock) result.push(currentBlock);
        currentBlock = { speaker: speakerKey, start: s.start, sentences: [], indexStart: i };
      }
      currentBlock.sentences.push(s);
    }
    if (currentBlock) result.push(currentBlock);
    return result;
  }, [mode, sentences]);

  // Auto-scroll the active sentence into the middle band (same throttling /
  // container-only-scroll trick we use for words). Reuses activeWordRef as
  // the "active element" pointer — element type doesn't matter, only its
  // position relative to scrollRef.
  useEffect(() => {
    if (mode !== 'sentences' || activeSentenceIndex < 0) return;
    const now = Date.now();
    if (now - lastScrollTimeRef.current < 200) return;
    lastScrollTimeRef.current = now;
    const el = activeWordRef.current;
    if (!el || !scrollRef.current) return;
    const container = scrollRef.current;
    const elRect = el.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const relativeTop = elRect.top - containerRect.top;
    const containerHeight = containerRect.height;
    if (relativeTop < containerHeight * 0.2 || relativeTop > containerHeight * 0.8) {
      const elCenterInContainer = (elRect.top - containerRect.top) + elRect.height / 2;
      const containerCenter = containerHeight / 2;
      const targetScrollTop = container.scrollTop + (elCenterInContainer - containerCenter);
      container.scrollTo({ top: targetScrollTop, behavior: 'smooth' });
    }
  }, [mode, activeSentenceIndex]);

  const formatTime = (sec) => {
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60);
    return `${m}:${s.toString().padStart(2, '0')}`;
  };

  const handleWordClick = useCallback((start) => {
    if (onSeek) onSeek(start);
  }, [onSeek]);

  const startEditLabels = () => {
    setEditingLabels({ ...(speakerLabels || {}) });
  };

  const saveLabels = async () => {
    if (onSpeakerLabelsChange) {
      try { await onSpeakerLabelsChange(editingLabels); } catch (e) { /* handled upstream */ }
    }
    setEditingLabels(null);
  };

  const swapLabels = () => {
    const keys = Object.keys(editingLabels || speakerLabels || {});
    if (keys.length < 2) return;
    const [k0, k1] = keys;
    const current = editingLabels || speakerLabels || {};
    const swapped = { ...current, [k0]: current[k1], [k1]: current[k0] };
    setEditingLabels(swapped);
  };

  if (mode === 'sentences') {
    if (!sentences || sentences.length === 0) {
      return (
        <div className="text-sm text-gray-400 italic p-4 text-center" data-testid="karaoke-no-translation">
          Перевод ещё не готов.
        </div>
      );
    }
  } else if (!words || words.length === 0) {
    return (
      <div className="text-sm text-gray-400 italic p-4 text-center" data-testid="karaoke-no-words">
        Для этой транскрипции нет word-timestamps.
        Запусти транскрипцию заново, чтобы получить синхрон с аудио.
      </div>
    );
  }

  const speakerKeys = Object.keys(speakerLabels || {});

  return (
    <div className="space-y-3" data-testid="karaoke-view">
      {/* Speaker labels controls */}
      {speakerKeys.length >= 1 && (
        <div className="flex items-center space-x-2 text-xs text-gray-400 flex-wrap gap-y-1" data-testid="karaoke-speakers">
          <Users size={14} />
          {editingLabels ? (
            <>
              {Object.entries(editingLabels).map(([id, label]) => (
                <input
                  key={id}
                  type="text"
                  value={label}
                  onChange={(e) => setEditingLabels({ ...editingLabels, [id]: e.target.value })}
                  className="bg-gray-700 text-white px-2 py-0.5 rounded text-xs w-20"
                  data-testid={`speaker-edit-${id}`}
                />
              ))}
              {speakerKeys.length >= 2 && (
                <button onClick={swapLabels} className="p-1 text-gray-400 hover:text-orange-400" title="Поменять местами" data-testid="speaker-swap">
                  <ArrowLeftRight size={14} />
                </button>
              )}
              <button onClick={saveLabels} className="p-1 text-green-400 hover:text-green-300" title="Сохранить" data-testid="speaker-save">
                <Check size={14} />
              </button>
            </>
          ) : (
            <>
              {speakerKeys.map((id) => (
                <span key={id} className="bg-gray-700 text-orange-300 px-2 py-0.5 rounded" data-testid={`speaker-label-${id}`}>
                  {speakerLabels[id]}
                </span>
              ))}
              {/* "изменить" is read-only for callers that don't pass an
                  onSpeakerLabelsChange handler (guests via share link) —
                  the backend mutation needs JWT, so the button would just
                  fail anyway. Hiding it keeps the UI honest. */}
              {onSpeakerLabelsChange && (
                <button onClick={startEditLabels} className="text-gray-500 hover:text-orange-400 text-xs underline" data-testid="speaker-edit-btn">
                  изменить
                </button>
              )}
            </>
          )}
        </div>
      )}

      {/* Transcript with karaoke highlight */}
      <div
        ref={scrollRef}
        className="bg-gray-900 rounded p-3 max-h-[60vh] md:max-h-96 overflow-y-auto leading-loose text-base md:text-base"
        data-testid="karaoke-scroll"
      >
        {mode === 'sentences' ? (
          // === Sentence-level karaoke (translation track) ===
          // No per-word highlight (Russian words don't align 1:1 with source
          // language tokens). Active sentence's whole text is highlighted; all
          // other sentences in the same speaker-block are visible above/below
          // for context, click any to seek.
          sentenceBlocks.map((block, bi) => {
            const labelsToUse = editingLabels || speakerLabels || {};
            const speakerLabel = block.speaker !== '' ? labelsToUse[block.speaker] : null;
            return (
              <div key={bi} className="mb-4" data-testid={`karaoke-translation-block-${bi}`}>
                {speakerLabel && (
                  <div className="text-xs font-semibold text-orange-400 mb-1">
                    {speakerLabel} · {formatTime(block.start)}
                  </div>
                )}
                <div className="space-y-1.5">
                  {block.sentences.map((s, si) => {
                    const globalIdx = block.indexStart + si;
                    const isActive = globalIdx === activeSentenceIndex;
                    const isPast = globalIdx < activeSentenceIndex;
                    return (
                      <p
                        key={si}
                        ref={isActive ? activeWordRef : null}
                        onClick={() => handleWordClick(s.start)}
                        className={`cursor-pointer transition-colors duration-150 rounded px-1.5 py-0.5 ${
                          isActive
                            ? 'bg-yellow-300 text-black font-semibold shadow-lg'
                            : isPast
                              ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                              : 'text-gray-100 hover:text-orange-300 hover:bg-gray-700'
                        }`}
                        data-testid={isActive ? 'karaoke-active-sentence' : undefined}
                      >
                        {s.text}
                      </p>
                    );
                  })}
                </div>
              </div>
            );
          })
        ) : (
          // === Word-level karaoke (original-language track, default) ===
          blocks.map((block, bi) => {
            const labelsToUse = editingLabels || speakerLabels || {};
            const speakerLabel = block.speaker !== '' ? labelsToUse[block.speaker] : null;
            return (
              <div key={bi} className="mb-4" data-testid={`karaoke-block-${bi}`}>
                {speakerLabel && (
                  <div className="text-xs font-semibold text-orange-400 mb-1">
                    {speakerLabel} · {formatTime(block.start)}
                  </div>
                )}
                <div>
                  {block.words.map((w, wi) => {
                    const globalIdx = block.indexStart + wi;
                    const isActive = globalIdx === activeIndex;
                    const isPast = globalIdx < activeIndex;
                    return (
                      <span
                        key={wi}
                        ref={isActive ? activeWordRef : null}
                        onClick={() => handleWordClick(w.start)}
                        className={`cursor-pointer transition-colors duration-150 rounded px-0.5 ${
                          isActive
                            ? 'bg-yellow-300 text-black font-semibold shadow-lg'
                            : isPast
                              ? 'text-gray-400 hover:text-white hover:bg-gray-700'
                              : 'text-gray-100 hover:text-orange-300 hover:bg-gray-700'
                        }`}
                        data-testid={isActive ? 'karaoke-active-word' : undefined}
                      >
                        {w.word}{' '}
                      </span>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="text-xs text-gray-500 italic text-center">
        {mode === 'sentences'
          ? 'Клик по предложению → перемотать на это место'
          : 'Клик по слову → перемотать на это место'}
      </div>
    </div>
  );
};

export default React.memo(KaraokeView);
